import { GripVertical, Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useCreateDemandReviewExhibitMutation,
  useDeleteDemandReviewExhibitMutation,
  useListDemandReviewExhibitsQuery,
} from '@/services/api/demandReviewService';
import { DemandReviewExhibit } from '@/services/types/demand-review-types';

import { ImportExhibitsDialog } from './ImportExhibitsDialog';

interface ExhibitManagerProps {
  matterId: string;
  workspaceId: string;
}

export const ExhibitManager = ({
  matterId,
  workspaceId,
}: ExhibitManagerProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newExhibitName, setNewExhibitName] = useState('');
  const [newExhibitBlobId, setNewExhibitBlobId] = useState('');

  const { data: exhibits = [], isLoading } = useListDemandReviewExhibitsQuery({
    matterId,
    workspaceId,
  });

  const [createExhibit, { isLoading: isCreating }] =
    useCreateDemandReviewExhibitMutation();
  const [deleteExhibit, { isLoading: isDeleting }] =
    useDeleteDemandReviewExhibitMutation();

  const handleAddExhibit = async () => {
    if (!newExhibitName.trim() || !newExhibitBlobId.trim()) return;

    try {
      await createExhibit({
        matterId,
        workspaceId,
        data: {
          name: newExhibitName,
          blob_id: newExhibitBlobId,
        },
      }).unwrap();

      setNewExhibitName('');
      setNewExhibitBlobId('');
      setIsAddDialogOpen(false);
    } catch (error) {
      console.error('Failed to add exhibit:', error);
    }
  };

  const handleDeleteExhibit = async (exhibitId: string) => {
    try {
      await deleteExhibit({ matterId, workspaceId, exhibitId }).unwrap();
    } catch (error) {
      console.error('Failed to delete exhibit:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-sm">Exhibits</CardTitle>
        <div className="flex gap-2">
          <ImportExhibitsDialog matterId={matterId} workspaceId={workspaceId} />
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1">
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Exhibit</DialogTitle>
                <DialogDescription>
                  Add an existing file as an exhibit for this demand review.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Exhibit Name</Label>
                  <Input
                    id="name"
                    value={newExhibitName}
                    onChange={(e) => setNewExhibitName(e.target.value)}
                    placeholder="e.g., Medical Records - Dr. Smith"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="blob_id">Blob ID</Label>
                  <Input
                    id="blob_id"
                    value={newExhibitBlobId}
                    onChange={(e) => setNewExhibitBlobId(e.target.value)}
                    placeholder="UUID of the file"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the Blob ID of an existing file in this matter.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddExhibit}
                  disabled={
                    isCreating ||
                    !newExhibitName.trim() ||
                    !newExhibitBlobId.trim()
                  }
                >
                  {isCreating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Add Exhibit
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {exhibits.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No exhibits added yet. Add exhibits to match citations.
          </div>
        ) : (
          <ScrollArea className="h-full max-h-[calc(100vh-350px)]">
            <div className="space-y-2">
              {exhibits.map((exhibit, index) => (
                <ExhibitItem
                  key={exhibit.id}
                  exhibit={exhibit}
                  index={index}
                  onDelete={() => handleDeleteExhibit(exhibit.id)}
                  isDeleting={isDeleting}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

interface ExhibitItemProps {
  exhibit: DemandReviewExhibit;
  index: number;
  onDelete: () => void;
  isDeleting: boolean;
}

const ExhibitItem = ({
  exhibit,
  index,
  onDelete,
  isDeleting,
}: ExhibitItemProps) => {
  return (
    <div className="group flex items-center gap-2 rounded-lg border bg-muted/30 p-3">
      <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10 text-xs font-semibold text-primary">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-medium">{exhibit.name}</p>
        <p className="text-xs text-muted-foreground">
          {exhibit.page_count} pages
        </p>
      </div>
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={onDelete}
        disabled={isDeleting}
      >
        {isDeleting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Trash2 className="h-4 w-4 text-destructive" />
        )}
      </Button>
    </div>
  );
};
